/* eslint complexity: 0 */

import React, { MutableRefObject, useCallback, useContext, useMemo, useRef } from 'react';

import { Link, Typography } from '@vtblife/uikit';

import {
    DeveloperCard,
    NewBuildingConstructionProgress,
    NewBuildingWhitelabelEnum,
    RouteParams
} from '@search/graphql-typings';

import { useStaticPathConfig } from '@search/common-core/src/StaticPathContext';
import classname from '@search/classname/src';
import Breadcrumb from '@search/vtbeco-ui/src/components/Breadcrumb';

import { dataLayerPush } from '@search/vtbeco-frontend-core/domain/google/dataLayerPush';

import {
    ImageLazySrcSetProvide,
    useClImageLazySrcSetUrl
} from '@search/vtbeco-frontend-core/domain/image/lazy/ImageLazySrcSet';

import { INewBuildingCardRightBlockProps } from '../../../../instances/m2/components/desktop/NewBuildingCard/NewBuildingCardRightBlock';
import { INewBuildingCardTopStickyProps } from '../../../../instances/vtb/components/desktop/NewBuildingCard/NewBuildingCardTopSticky';
import { useDeveloperNames } from '../../../common/components/NewBuildingCard';
import { GlobalContext, IOnChangeGlobalContext, OnChangeGlobalContext } from '../../../common/components/GlobalContext';
import { PhoneButtonStateProvider } from '../../../common/components/NewBuildingCard/PhoneButtonStateProvider';
import { ChatStateProvider } from '../../../common/components/NewBuildingCard/ChatStateProvider';
import { useBreadcrumbs } from '../../../common/components/NewBuildingCardPage/useBreadcrumbs';
import type { NewBuildingConstructionQuery$data as NewBuildingConstructionQueryResponse } from '../../../common/components/NewBuildingConstruction/__generated__/NewBuildingConstructionQuery.graphql';
import { usePhoneButton } from '../../../common/hooks/usePhoneButton';

import NewBuildingCardHeading from '../NewBuildingCard/NewBuildingCardHeading';

import ConstructionProgress from './NewBuildingConstructionProgress';

import './styles.css';

interface INewBuildingConstructionProps {
    newBuilding: NewBuildingConstructionQueryResponse['newBuilding'];
    isLoading: boolean;
    cardURL: string;
}

const cn = classname.bind(null, 'NewBuildingConstruction');

const NewBuildingConstruction: React.FunctionComponent<INewBuildingConstructionProps> = ({
    newBuilding,
    isLoading,
    cardURL
}) => {
    const {
        instanceConfig: {
            hasBreadcrumbs,
            hasStickyNavbar,
            searchIndex
        },
        components: {
            NewBuildingCardRightBlock: NewBuildingCardRightBlockComponent
        }
    } = useContext(GlobalContext);

    const NewBuildingCardRightBlock = NewBuildingCardRightBlockComponent as React.FunctionComponent<INewBuildingCardRightBlockProps>;
    const NewBuildingCardRightBlockVtb = NewBuildingCardRightBlockComponent as React.FunctionComponent<INewBuildingCardTopStickyProps>;

    const staticPath = useStaticPathConfig();
    const onChangeGlobalContext = useContext<IOnChangeGlobalContext>(OnChangeGlobalContext);

    const breadcrumbs = useBreadcrumbs({
        narrowRegionId: newBuilding.narrowRegionId,
        narrowRegionLocative: newBuilding.narrowRegionLocative,
        routeParams: newBuilding.routeParams as RouteParams,
        title: newBuilding.title.nominative,
        isConstruction: true
    });

    const firstDeveloper = newBuilding.developerCards[0]?.developer;
    const developerLink = useMemo(() => onChangeGlobalContext.linkBuilder.developer &&
        onChangeGlobalContext.linkBuilder.developer({
            id: firstDeveloper!.id,
            name: firstDeveloper!.translitTitle
        }), [ firstDeveloper, onChangeGlobalContext.linkBuilder ]);

    const developerVtb = firstDeveloper?.image?.small ? {
        title: firstDeveloper.title,
        imageUrl: firstDeveloper.image.small
    } : undefined;

    const {
        hasApartments,
        hasApartmentsOnly
    } = newBuilding;

    const { constructionProgress, ranking } = newBuilding;
    const hasConstructionProgress = constructionProgress.length > 0;

    const onStatEventPush = useCallback(() => dataLayerPush({
        event: 'nb_call_like',
        nb_price: newBuilding.priceMin ?? 0,
        nb_class: ranking ?? ''
    }), [ newBuilding.priceMin, ranking ]);

    const phoneButtonState = usePhoneButton({
        newBuildingId: newBuilding.id,
        developerCards: newBuilding.developerCards,
        phoneNum: 0,
        onStatEventPush
    });

    const developerNames = useDeveloperNames(newBuilding.developerCards as DeveloperCard[]);

    const titleSeoTail = useMemo(
        () => newBuilding.seo.seoTitle ? ` (${newBuilding.seo.seoTitle})` : '',
        [ newBuilding.seo.seoTitle ]
    );

    const stickyRef = useRef<HTMLDivElement>(null);

    const getUrl = useClImageLazySrcSetUrl({ baseUrl: newBuilding.gallery?.baseUrl });

    if (isLoading) {
        return null;
    }

    return (
        <>
            {hasBreadcrumbs && breadcrumbs && (
                <div className={cn('breadcrumbs')}>
                    <Breadcrumb sections={breadcrumbs.items.map(({
                        title: content,
                        path: href,
                        active
                    }) => ({
                        content,
                        href,
                        active
                    }))} />
                </div>
            )}
            <PhoneButtonStateProvider {...phoneButtonState}>
                <ChatStateProvider initialClientMessage={
                    `Интересует ход строительства ${newBuilding.title.genitive}`
                }>
                    <div className={cn('main')}>
                        <div className={cn('left')}>
                            <NewBuildingCardHeading newBuildingTitle={
                                `Ход строительства ${newBuilding.title.genitive}`
                            } />
                            <Typography
                                className={cn('info')}
                                variant='primary-alone'
                            >
                                <Link href={cardURL}>
                                    На страницу комплекса
                                </Link>
                                {newBuilding.buildingStats.text ? (
                                    <span>
                                        {newBuilding.buildingStats.text}
                                    </span>
                                ) : null}
                            </Typography>
                            {hasConstructionProgress && (
                                <div className={cn('construction')}>
                                    <ImageLazySrcSetProvide getUrl={getUrl}>
                                        <ConstructionProgress
                                            items={constructionProgress as NewBuildingConstructionProgress[]}
                                            title={newBuilding.title}
                                            titleSeoTail={titleSeoTail}
                                        />
                                    </ImageLazySrcSetProvide>
                                </div>
                            )}
                        </div>
                        <div className={cn('right')}>
                            { searchIndex === NewBuildingWhitelabelEnum.Default ? (
                                <NewBuildingCardRightBlock
                                    developerLink={developerLink ?? undefined}
                                    developer={firstDeveloper}
                                    priceMin={newBuilding.priceMin}
                                    priceMax={newBuilding.priceMax}
                                    pricePerM2Min={newBuilding.pricePerM2Min}
                                    pricePerM2Max={newBuilding.pricePerM2Max}
                                    newBuildingId={newBuilding.id}
                                    isMskMO={newBuilding.isMskMO}
                                    isSPbLO={newBuilding.isSPbLO}
                                    isSpecProjectRegion={newBuilding.isSpecProjectRegion}
                                    hasChat={newBuilding.hasChat}
                                    newBuildingName={newBuilding.name}
                                    newBuildingType={newBuilding.type}
                                    newBuildingTitle={newBuilding.title.nominative}
                                    newBuildingAddress={newBuilding.address}
                                    staticPath={staticPath}
                                    buildingClass={newBuilding.buildingClass}
                                    buildingStatus={newBuilding.buildingStatus}
                                    areaRange={newBuilding.formatAreaRange}
                                    developerNames={developerNames}
                                    hasApartments={hasApartments}
                                    hasApartmentsOnly={hasApartmentsOnly}
                                    isSoldOut={newBuilding.isSoldOut}
                                    isSoon={newBuilding.isSoon}
                                    stickyRef={hasStickyNavbar ? stickyRef as MutableRefObject<HTMLDivElement> : undefined}
                                    isFavorite={Boolean(newBuilding.isFavorite)}
                                    m2Pro={newBuilding.m2Pro}
                                />
                            ) : (
                                <NewBuildingCardRightBlockVtb
                                    developer={developerVtb}
                                    priceMin={newBuilding.priceMin}
                                    pricePerM2Min={newBuilding.pricePerM2Min}
                                    hasApartments={hasApartments}
                                />
                            )}
                        </div>
                    </div>
                </ChatStateProvider>
            </PhoneButtonStateProvider>
        </>
    );
};

NewBuildingConstruction.displayName = 'NewBuildingConstruction';

export default NewBuildingConstruction;
